/**
 * IT > Service Desk > Settings (/it/service-desk/settings). Everything about how
 * the desk behaves lives in the database and is edited here: the category tree
 * with its fields and follow-up questions, the deadlines, the hours, topics, and
 * automations. The active tab is kept in the address (?tab=) so a link opens
 * straight to it.
 */

import { HelpCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { openHelpForPage } from '@/lib/helpLauncher';
import { ApproversSection } from './ApproversSection';
import { AutomationsTab } from './AutomationsTab';
import { CategoriesTab } from './CategoriesTab';
import { DeskTab } from './DeskTab';
import { SdShell } from './SdShell';
import { SlaTab } from './SlaTab';
import { TopicsTab } from './TopicsTab';

const TABS = [
  { id: 'categories', label: 'Categories' },
  { id: 'sla', label: 'Deadlines' },
  { id: 'desk', label: 'Desk' },
  { id: 'topics', label: 'Topics' },
  { id: 'automations', label: 'Automations' },
] as const;
type TabId = (typeof TABS)[number]['id'];

/** The Help Center article for this page is filed under this page key. */
const SETTINGS_HELP_PAGE_KEY = 'cockpit:it/service-desk';

export function Settings() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: TabId = TABS.some((t) => t.id === raw) ? (raw as TabId) : 'categories';

  return (
    <SdShell
      title="Service Desk settings"
      subtitle="Change how tickets are filed, how fast they are due, and what happens automatically."
      tab="settings"
      tabsAside={
        <button
          type="button"
          onClick={() => void openHelpForPage(SETTINGS_HELP_PAGE_KEY)}
          className="inline-flex items-center gap-1.5 rounded-md text-[13px] font-semibold text-amp-olive-deep underline-offset-2 hover:underline dark:text-amp-olive-dark"
        >
          <HelpCircle className="h-4 w-4" aria-hidden />
          How these settings work
        </button>
      }
    >
      <div role="tablist" aria-label="Settings sections" className="mb-4 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={t.id === tab}
            onClick={() => setParams({ tab: t.id }, { replace: true })}
            className={
              t.id === tab
                ? 'rounded-pill bg-amp-olive-deep px-3 py-1 text-[13px] font-semibold text-amp-cream'
                : 'rounded-pill border border-amp-line px-3 py-1 text-[13px] font-semibold text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm'
            }
          >
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel">
        {tab === 'categories' && <CategoriesTab />}
        {tab === 'sla' && <SlaTab />}
        {tab === 'desk' && (
          <>
            <DeskTab />
            <ApproversSection />
          </>
        )}
        {tab === 'topics' && <TopicsTab />}
        {tab === 'automations' && <AutomationsTab />}
      </div>
    </SdShell>
  );
}
